import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { ToastService } from "src/app/core/services/behavior/toast.service";
import { Subscription, Observable } from "rxjs";
import { startWith, map } from "rxjs/operators";
import { CadastroAdicionaisService } from "src/app/core/services/behavior/cadastro-adicionais.service";
import { ProdutoService } from "src/app/core/services/http/produto.service";
import { AdicionalFamiliaRequest, AdicionalProdutoRequest, AdicionalProdutoViewModel, ProdutoResponse } from "src/app/shared/models/produto.model";

@Component({
    selector: 'app-cadastro-adicionais',
    templateUrl: './cadastro-adicionais.page.html',
    styleUrls: ['./cadastro-adicionais.page.scss'],
})
export class CadastroAdicionaisPage implements OnInit, OnDestroy {
    private codigoProduto: number = 0;
    private codigoFamilia: number = 0;
    public ehFamilia: boolean = false;
    public ehProduto: boolean = false;

    public adicionalCtrl = new FormControl('');
    public adicionaisFiltrados!: Observable<AdicionalProdutoViewModel[]>;
    public adicionaisAdicionados: AdicionalProdutoViewModel[] = [];
    public listaAdicionais: AdicionalProdutoViewModel[] = [];
    public listaProdutos: ProdutoResponse[] = [];

    public precoAdicional: string = "";
    public adicionandoAdicional: boolean = false;

    private subscriptions: Subscription[] = [];

    constructor(
        private produtoService: ProdutoService,
        private cadastroAdicionaisService: CadastroAdicionaisService,
        private toastService: ToastService
    ) {
        const codigoProdutoSub = this.cadastroAdicionaisService.codigoProduto.subscribe((res) => {
            this.codigoProduto = res;
            this.ehProduto = res > 0;
        });

        const codigoFamiliaSub = this.cadastroAdicionaisService.codigoFamilia.subscribe((res) => {
            this.codigoFamilia = res;
            this.ehFamilia = res > 0;
            
            if (this.ehFamilia) {
                this.carregarAdicionaisFamilia();
            }
        });

        this.subscriptions.push(codigoProdutoSub, codigoFamiliaSub);

        this.adicionaisFiltrados = this.adicionalCtrl.valueChanges.pipe(
            startWith(null),
            map((adicional: string | null) => (adicional ? this.filtrarAdicionais(adicional) : this.listaAdicionais.slice())),
        );
    }

    ngOnInit(): void {
        this.produtoService.getAdicionais().subscribe(res => {
            if (res.success) {
                this.listaAdicionais = res.data.map(item => ({
                    nome: item.nome,
                    codigo: item.codigo,
                    precoAdicional: item.precoAdicional || 0
                } as AdicionalProdutoViewModel));
                this.adicionalCtrl.setValue(this.adicionalCtrl.value);
            }
        });

        this.produtoService.getAllComExcluidos().subscribe({
            next: (response) => {
                if (response.success) {
                    const data = response.data as any;
                    this.listaProdutos = data.produtos as ProdutoResponse[];
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }

    private carregarAdicionaisFamilia() {
        this.produtoService.getAdicionaisPorFamilia(this.codigoFamilia).subscribe(res => {
            if (res.success) {
                this.adicionaisAdicionados = res.data.map(item => ({
                    nome: item.nome,
                    codigo: item.codigo,
                    precoAdicional: item.precoAdicional || 0
                } as AdicionalProdutoViewModel));
            }
        });
    }

    public alterarPrecoAdicional(event: any) {
        const preco = event.target.value;
        if (preco) {
            this.precoAdicional = preco;
        }
    }

    public addAdicional(event: any): void {
        const value = this.adicionalCtrl.value?.trim();
        if (!value || value.length === 0) return;

        const precoAdicionalNumber: number = Number(this.precoAdicional.replace(/\./g, '').replace(',', '.'));

        this.salvarAdicional(value, precoAdicionalNumber);
    }

    public selectedAdicional(event: any): void {
        const adicional = this.listaAdicionais.find(i => i.nome === event.option.value);
        if (!adicional) return;

        this.salvarAdicional(adicional.nome, adicional.precoAdicional || 0);
    }

    private salvarAdicional(nomeAdicional: string, preco: number) {
        const adicionalExistente = this.adicionaisAdicionados.some(i => i.nome === nomeAdicional);

        if (adicionalExistente) {
            this.adicionalCtrl.setValue(null);
            this.precoAdicional = "";
            return;
        }

        if (this.ehFamilia) {
            this.adicionandoAdicional = true;
            const req = new AdicionalFamiliaRequest();
            req.nome = nomeAdicional;
            req.codigoFamilia = this.codigoFamilia;
            req.precoAdicional = preco;

            this.produtoService.incluirAdicionalFamilia(req).subscribe({
                next: (res) => {
                    this.adicionandoAdicional = false;
                    if (res.success) {
                        this.adicionaisAdicionados.push({ nome: nomeAdicional, codigo: 0, precoAdicional: preco });
                        this.limparForm();
                    }
                },
                error: () => this.adicionandoAdicional = false
            });
        }
    }

    public removerAdicional(adicional: AdicionalProdutoViewModel): void {
        if (this.ehFamilia) {
            const req = new AdicionalFamiliaRequest();
            req.nome = adicional.nome;
            req.codigoFamilia = this.codigoFamilia;

            this.produtoService.removerAdicionalFamilia(req).subscribe(res => {
                if (res.success) {
                    const index = this.adicionaisAdicionados.indexOf(adicional);
                    if (index >= 0) this.adicionaisAdicionados.splice(index, 1);
                }
            });
        }
    }

    private filtrarAdicionais(value: string): AdicionalProdutoViewModel[] {
        return this.listaAdicionais.filter(adicional => adicional.nome.toLowerCase().includes(value.toLowerCase()));
    }

    private limparForm() {
        this.adicionalCtrl.setValue(null);
        this.precoAdicional = "";
        
        // Broadcast an event or find a way to clean the child via viewchild if we need,
        // Actually, just clearing precoAdicional will be bound if we pass it, but preco input is local to the dumb component
        // So we can let the parent wait or just do this.
    }

    get desabilitarPrecoAdicional(): boolean {
        const value = this.adicionalCtrl?.value;
        if (!value) return false;
        
        const existsInAdicionais = this.listaAdicionais.some(a => a.nome.toLowerCase() === value.toLowerCase());
        const existsInProdutos = this.listaProdutos.some(p => p.nome.toLowerCase() === value.toLowerCase());
        return existsInAdicionais || existsInProdutos;
    }
}
